import { Fragment } from "react";
import { Link, useParams } from "react-router";
import { Button } from "@progress/kendo-react-buttons";
import { Grid, GridCellProps, GridColumn } from "@progress/kendo-react-grid";
import { useDealReward } from "src/dm/hooks/useDealRewards";
import { useRewardBilling } from "src/dm/hooks/useDealRewardSub";
import { useCurrentUser } from "src/dm/auth";
import { useDealContext } from "src/dm/components/DealLayout/DealLayout";
import { usePromoInfo } from "src/dm/hooks/useLookups";
import { useMarketingMessagesWithFlags } from "src/dm/hooks/useMarketingMessagesWithFlags";
import {
  useRewardBuyQtyCodes,
  useRewardGetQtyCodes,
} from "src/dm/hooks/useRewardQtyCodes";
import * as s from "./ViewReward.css";

// Read-only counterpart of EditReward.aspx (legacy ViewReward.aspx).
// Sections — same order as the Edit page so the user toggles between
// the two without re-orienting:
//   - Reward Header
//   - Vendor Liability Statement (external user / external-vendor deal)
//   - Details (internal users)
//   - Customer Saves
//   - Ad Block Information
//   - Tag Information
//   - Reward Billing grid (always, just like legacy)
//
// DealReward is 1:1 with Deal — the URL is `/dm/deals/:dealId/reward/view`
// (no reward id). When the deal has no reward yet, we show a friendly
// "No reward configured" notice with a link to the editor (which has the
// "Create Reward" CTA).

const fmtCurrency = (n: number | null | undefined): string =>
  n == null ? "—" : n.toLocaleString(undefined, { style: "currency", currency: "USD" });

const fmtPct = (n: number | null | undefined): string =>
  n == null ? "—" : `${n.toLocaleString(undefined, { maximumFractionDigits: 2 })}%`;

const fmt = (v: unknown): string =>
  v == null || v === "" ? "—" : String(v);

/**
 * One label/value pair inside a `.dl` grid. The fragment keeps the
 * `<dt>` and `<dd>` as direct grid children so the 180/1fr columns line
 * up across every section.
 */
const Row = ({
  label,
  value,
  suffix,
}: {
  label: string;
  value: string;
  suffix?: string;
}) => (
  <Fragment>
    <dt className={s.dt}>{label}</dt>
    <dd className={s.dd}>
      {value}
      {suffix ? <span className={s.muted}> {suffix}</span> : null}
    </dd>
  </Fragment>
);

/**
 * Full-row multiline value (Ad Block comments, etc) — the label stays in
 * column 1, the value spans column 2 only but wraps via `white-space:
 * pre-wrap` so long text doesn't blow up the grid.
 */
const RowMultiline = ({
  label,
  value,
}: {
  label: string;
  value: string | null | undefined;
}) => (
  <Fragment>
    <dt className={s.dt}>{label}</dt>
    <dd className={s.ddMulti}>{value == null || value === "" ? "—" : value}</dd>
  </Fragment>
);

const ViewReward = () => {
  const { dealId: dealIdParam } = useParams<{ dealId: string }>();
  const dealId = dealIdParam ? Number(dealIdParam) : undefined;

  const deal = useDealContext();
  const { data: user } = useCurrentUser();
  const isInternalUser = user?.isInternalUser ?? false;
  const isExternalUser = user?.isExternalUser ?? false;
  const showVendorLiability = isExternalUser || !deal.isInternalDeal;
  const showInternalDetails = isInternalUser;

  const rewardQ = useDealReward(dealId);
  const billingQ = useRewardBilling(dealId);
  const buyCodesQ = useRewardBuyQtyCodes();
  const getCodesQ = useRewardGetQtyCodes();
  const marketingMessagesQ = useMarketingMessagesWithFlags();
  const promoInfoQ = usePromoInfo(rewardQ.data?.promotionCode ?? undefined);

  if (dealId === undefined) return <p>Invalid URL.</p>;
  if (rewardQ.isLoading) return <p>Loading reward…</p>;
  if (rewardQ.isError) {
    return (
      <p className={s.errorText}>
        Could not load reward:{" "}
        {rewardQ.error instanceof Error ? rewardQ.error.message : "unknown"}
      </p>
    );
  }
  if (!rewardQ.data) {
    return (
      <div className={s.wrap}>
        <h2 className={s.title}>Reward</h2>
        <p>This deal does not have a reward configured yet.</p>
        <Link to={`/dm/deals/${dealId}/reward`}>
          <Button themeColor="primary">Go to reward editor</Button>
        </Link>
      </div>
    );
  }

  const r = rewardQ.data;
  const buyCode = buyCodesQ.data?.find((c) => c.id === r.vendorBuyQtyCode);
  const getCode = getCodesQ.data?.find((c) => c.id === r.vendorGetQtyCode);
  const mm = marketingMessagesQ.data?.find(
    (m) => m.id === r.marketingMessageCode
  );

  const rewardType = (() => {
    if (!promoInfoQ.data) return "—";
    if (promoInfoQ.data.isBogo) return "BOGO";
    if (promoInfoQ.data.isRewardAmountOff) return "Reward Amount Off";
    if (promoInfoQ.data.isRewardPromo) return "Reward at Group Level";
    return "Standard";
  })();

  return (
    <div className={s.wrap}>
      <Link to={`/dm/deals/${dealId}/reward`} className={s.backLink}>
        ← Back to reward editor
      </Link>

      <div className={s.titleRow}>
        <h2 className={s.title}>
          Reward
          {r.promotionId ? (
            <span className={s.subtitle}> (Prom Id {r.promotionId})</span>
          ) : null}
        </h2>
        <div className={s.titleActions}>
          <Link to={`/dm/deals/${dealId}/reward`}>
            <Button themeColor="primary">Edit Reward</Button>
          </Link>
          <Link to={`/dm/deals/${dealId}/reward/items`}>
            <Button>Reward Items</Button>
          </Link>
          {r.autoBilling && (
            <Link to={`/dm/deals/${dealId}/reward/billing`}>
              <Button>Reward Billing</Button>
            </Link>
          )}
        </div>
      </div>

      {r.posPublished === true && (
        <div className={s.posBanner}>
          POS Maintenance has been generated for this reward. It cannot be
          edited or deleted. Contact Deal Central Support for assistance.
        </div>
      )}

      {/* Header */}
      <section className={s.card}>
        <h3 className={s.sectionTitle}>Reward Header</h3>
        <dl className={s.dl}>
          <Row label="Reward Description" value={fmt(r.description)} />
          <Row label="Prom Id" value={fmt(r.promotionId)} />
        </dl>
      </section>

      {showVendorLiability && (
        <section className={s.card}>
          <h3 className={s.sectionTitle}>Vendor Liability Statement</h3>
          <dl className={s.dl}>
            <Row
              label="Buy"
              value={
                r.vendorBuyQty == null
                  ? "—"
                  : `${r.vendorBuyQty} ${buyCode?.description ?? r.vendorBuyQtyCode ?? ""}`
              }
            />
            <Row
              label="For ($) — Vendor Net Reward"
              value={fmtCurrency(r.vendorNetAmount)}
            />
            <Row
              label="Get — Vendor Get Qty"
              value={
                r.vendorGetQty == null
                  ? "—"
                  : `${r.vendorGetQty} ${getCode?.description ?? r.vendorGetQtyCode ?? ""}`
              }
            />
            <Row
              label="Vendor Agrees to Pay"
              value={
                r.vendorFundingAmount == null
                  ? "—"
                  : r.vendorFundingCode === "P"
                    ? fmtPct(r.vendorFundingAmount)
                    : fmtCurrency(r.vendorFundingAmount)
              }
              suffix="per reward"
            />
          </dl>
        </section>
      )}

      {showInternalDetails && (
        <section className={s.card}>
          <h3 className={s.sectionTitle}>Details</h3>
          <dl className={s.dl}>
            <Row
              label="Promo Code"
              value={
                r.promotionCode != null
                  ? `${r.promotionCode}${promoInfoQ.data?.description ? " — " + promoInfoQ.data.description : ""}`
                  : "—"
              }
            />
            <Row label="Reward Type" value={rewardType} />
            <Row
              label="Mandatory Purchase Scan Qty"
              value={`${fmt(r.scanQuantity)} (${r.anyEachCode === "E" ? "Each" : "Any"})`}
            />
            <Row label="Limit" value={fmt(r.dealLimit)} />
            <Row label="Charge Dept" value={fmt(r.chargeDepartmentCode)} />
            <Row
              label="Misc Account Code"
              value={fmt(r.miscellaneousAccountCode)}
            />
          </dl>
        </section>
      )}

      {showInternalDetails && (
        <section className={s.card}>
          <h3 className={s.sectionTitle}>Customer Saves</h3>
          <dl className={s.dl}>
            <Row label="Reward Item Qty" value={fmt(r.rewardQuantity)} />
            <Row
              label="Customer Reward Amount"
              value={fmtCurrency(r.rewardAmount)}
            />
            <Row label="Reward Percent Off" value={fmtPct(r.rewardPercent)} />
            <Row label="Automated Billing" value={r.autoBilling ? "Yes" : "No"} />
          </dl>
        </section>
      )}

      {showInternalDetails && (
        <section className={s.card}>
          <h3 className={s.sectionTitle}>Ad Block Information</h3>
          <dl className={s.dl}>
            <Row
              label="Do Not Create Ad Block"
              value={r.excludeFromAmp ? "Yes" : "No"}
            />
            <Row label="Ad Block ID" value={fmt(r.adBlockId)} />
            <Row
              label="Line of Business"
              value={fmt(r.adBlockLineOfBusinessCode)}
            />
            <Row
              label="Category Manager"
              value={fmt(r.adBlockCategoryManagerCode)}
            />
            <RowMultiline
              label="Coupon Comment"
              value={r.adBlockCouponComment}
            />
            <RowMultiline label="Store Comment" value={r.adBlockStoreComment} />
            <RowMultiline
              label="Merch Comment"
              value={r.adBlockMerchandisingComment}
            />
            <RowMultiline label="Ad Comment" value={r.adBlockAdComment} />
          </dl>
        </section>
      )}

      {showInternalDetails && (
        <section className={s.card}>
          <h3 className={s.sectionTitle}>Tag Information</h3>
          <dl className={s.dl}>
            <Row
              label="Marketing Message"
              value={
                mm
                  ? `${mm.id} — ${mm.description}`
                  : fmt(r.marketingMessageCode)
              }
            />
            <Row label="Buy Quantity" value={fmt(r.tagBuyQty)} />
            <Row label="Get Quantity" value={fmt(r.tagGetQty)} />
            <Row
              label="Tag Line 1"
              value={fmt(r.tagProductDescriptionLine1)}
            />
            <Row
              label="Tag Line 2"
              value={fmt(r.tagProductDescriptionLine2)}
            />
          </dl>
        </section>
      )}

      {/* Reward Billing grid — always rendered in legacy ViewReward */}
      <section className={s.card}>
        <h3 className={s.sectionTitle}>Reward Billing</h3>
        {billingQ.isLoading && <p className={s.muted}>Loading…</p>}
        {!billingQ.isLoading && (billingQ.data?.length ?? 0) === 0 && (
          <p className={s.muted}>No billing data to display.</p>
        )}
        {(billingQ.data?.length ?? 0) > 0 && (
          <Grid data={billingQ.data}>
            <GridColumn field="businessUnit" title="Business Unit" />
            <GridColumn field="customerId" title="AP Vendor" />
            <GridColumn field="addressSequence" title="Address" />
            <GridColumn field="entryType" title="Entry Type" />
            <GridColumn field="salesPerson" title="Sales Person" />
            <GridColumn
              title="Amount / Percent"
              cells={{
                data: (props: GridCellProps) => {
                  const row = props.dataItem as {
                    billingAmount: number | null;
                    billingPercent: number | null;
                  };
                  return (
                    <td>
                      {row.billingAmount != null
                        ? fmtCurrency(row.billingAmount)
                        : row.billingPercent != null
                          ? fmtPct(row.billingPercent)
                          : "—"}
                    </td>
                  );
                },
              }}
            />
            <GridColumn
              field="invoiceDescription"
              title="Invoice Description"
            />
          </Grid>
        )}
      </section>
    </div>
  );
};

export default ViewReward;
